import { useState, useEffect } from 'react'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'
import { toast } from 'sonner'

import { useUpdateInquiry } from '@/stores/leads.store'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ContactSelector } from '@/components/contacts/contact-selector'

interface SwapContactDialogProps {
  inquiryId: string
  currentContactId?: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * SwapContactDialog - Dialog for changing the contact associated with an inquiry
 *
 * Features:
 * - Uses ContactSelector for consistent contact selection/creation experience
 * - Updates inquiry contact via updateInquiryContact mutation
 * - Shows loading state during mutation
 * - Shows toast notifications on success/error
 * - Resets selection on close
 *
 * @param inquiryId - The inquiry ID to update
 * @param currentContactId - The currently associated contact ID
 * @param open - Control dialog visibility
 * @param onOpenChange - Handle open/close state changes
 */
export function SwapContactDialog({
  inquiryId,
  currentContactId,
  open,
  onOpenChange,
}: SwapContactDialogProps) {
  const [selectedContactId, setSelectedContactId] = useState<string>(currentContactId || '')
  const { updateInquiryContact, isLoading } = useUpdateInquiry()

  // Reset selection when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedContactId(currentContactId || '')
    }
  }, [open, currentContactId])

  const handleSwap = async () => {
    if (!selectedContactId || selectedContactId === currentContactId) {
      toast.error('Please select a different contact')
      return
    }

    try {
      await updateInquiryContact(inquiryId, selectedContactId)
      toast.success('Contact updated successfully')
      onOpenChange(false)
    } catch (error) {
      console.error('Failed to update contact:', error)
      reportError(error, { feature: 'inquiry_contact_swap', kind: 'mutation' })
      toast.error('Failed to update contact', {
        description: getErrorDescription(error),
      })
    }
  }

  const hasChanged = selectedContactId && selectedContactId !== currentContactId

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Change Contact</DialogTitle>
          <DialogDescription>
            Select a different contact to associate with this lead. You can also create a new contact.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <ContactSelector
            value={selectedContactId}
            onChange={setSelectedContactId}
            disabled={isLoading}
          />
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSwap}
            disabled={isLoading || !hasChanged}
          >
            {isLoading ? 'Updating...' : 'Update Contact'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
